import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn } from '../ui.jsx';

// Public marketing + pricing landing — shown for unauthenticated visitors on /, /pricing.
export function MarketingLanding() {
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);

  useEffect(() => {
    api('/api/billing/plans').then(r => { setPlans(r.plans||[]); setTrialDays(r.trialDays||14); }).catch(()=>{});
  }, []);

  const go = (plan) => {
    window.location.href = `/signup${plan ? `?plan=${plan}` : ''}`;
  };

  return <div style={{ minHeight:'100vh', background:G.bg, color:G.text }}>
    <Nav/>
    <Hero trialDays={trialDays} go={go}/>
    <Features/>
    <Pricing plans={plans} go={go}/>
    <Testimonials/>
    <Faq trialDays={trialDays}/>
    <Cta trialDays={trialDays} go={go}/>
    <Footer/>
  </div>;
}

function Nav() {
  return <nav style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 32px', borderBottom:`1px solid ${G.border}` }}>
    <a href="/" style={{ textDecoration:'none', fontSize:22, fontWeight:800, background:`linear-gradient(135deg,${G.purple},${G.cyan})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>TradeOps</a>
    <div style={{ display:'flex', alignItems:'center', gap:18, fontSize:14 }}>
      <a href="#features" style={navLink}>Features</a>
      <a href="#pricing" style={navLink}>Pricing</a>
      <a href="#faq" style={navLink}>FAQ</a>
      <a href="/login" style={navLink}>Sign in</a>
      <Btn v="primary" sz="sm" onClick={()=>window.location.href='/signup'}>Start free trial</Btn>
    </div>
  </nav>;
}
const navLink = { color:G.textMuted, textDecoration:'none', fontWeight:600 };

function Hero({ trialDays, go }) {
  return <section style={{ padding:'80px 32px 60px', textAlign:'center', maxWidth:980, margin:'0 auto' }}>
    <div style={{ display:'inline-block', padding:'6px 14px', borderRadius:99, background:G.purpleDim, color:G.purple, fontSize:12, fontWeight:700, letterSpacing:'.04em', marginBottom:22 }}>
      BUILT FOR HVAC, PLUMBING, ELECTRICAL + HOME SERVICES
    </div>
    <h1 style={{ fontSize:56, lineHeight:1.05, margin:'0 0 22px', fontWeight:800, letterSpacing:'-.02em' }}>
      Close <span style={{ background:`linear-gradient(135deg,${G.purple},${G.cyan})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>30% more jobs</span>.<br/>Get paid faster.
    </h1>
    <p style={{ fontSize:18, color:G.textMuted, maxWidth:660, margin:'0 auto 30px', lineHeight:1.55 }}>
      AI answers your missed calls, books appointments, and follows up automatically. Three-tier estimates lift your average ticket 50%. Customers pay online before the truck leaves the driveway.
    </p>
    <div style={{ display:'flex', gap:18, justifyContent:'center', flexWrap:'wrap', marginBottom:22, fontSize:13, color:G.textMuted }}>
      <span>📈 <strong style={{ color:G.green }}>+30%</strong> close rate</span>
      <span>💰 <strong style={{ color:G.green }}>+50%</strong> average ticket</span>
      <span>⚡ <strong style={{ color:G.green }}>30s</strong> lead response</span>
      <span>🤖 <strong style={{ color:G.green }}>24/7</strong> AI booking</span>
    </div>
    <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
      <Btn v="primary" sz="lg" onClick={()=>go()}>Start {trialDays}-day free trial</Btn>
      <Btn v="ghost" sz="lg" onClick={()=>document.getElementById('pricing')?.scrollIntoView({behavior:'smooth'})}>See pricing</Btn>
    </div>
    <div style={{ marginTop:18, fontSize:13, color:G.textMuted }}>
      No card required · Cancel anytime · Setup takes 15 minutes
    </div>
  </section>;
}

function Features() {
  const items = [
    { icon:'📞', title:'Never miss a lead again', desc:'AI answers missed calls + replies to texts in 30 seconds, 24/7. Books appointments directly onto your calendar. Contractors using this book 30-40% more leads.' },
    { icon:'📋', title:'Close 30% bigger tickets', desc:'Good/Better/Best estimates with AI-built tiers. Customers self-select the package — your average ticket goes from $1,400 to $2,100 overnight.' },
    { icon:'💳', title:'Get paid faster', desc:'Estimate → agreement signed → invoice sent → customer pays online. Card, ACH, or financing — three-day Net 30 average becomes same-day pay.' },
    { icon:'💸', title:'Financing built in', desc:'Wisetack, Sunbit, Affirm, Synchrony all live on every estimate. Customers pre-qualify in 30 seconds. Increases close rate 25%+ on tickets over $3,000.' },
    { icon:'🔧', title:'Smart dispatch', desc:'Skills + location + customer history picks the right tech every time. No more "Carlos isn\'t certified for this" calls at 4pm.' },
    { icon:'⚡', title:'30+ workflows on autopilot', desc:'Appointment reminders, estimate follow-ups, review requests, win-back campaigns. Set up once, run forever. Recover 15-20% of cold leads automatically.' },
    { icon:'🤝', title:'Sign agreements before the truck rolls', desc:'Service agreement auto-sends after estimate approval. Customer signs from their phone. Zero kitchen-table delays.' },
    { icon:'⭐', title:'5-star reviews on autopilot', desc:'After every completed job, smart-route review requests: 4-5 stars push to Google, 1-3 stars route to private feedback. Your public rating climbs, not drops.' },
    { icon:'📊', title:'Run by the numbers', desc:'Close rate, average ticket, tech leaderboards, ad ROAS, MRR forecasting. The dashboard you actually look at every morning.' },
    { icon:'🧠', title:'It learns. You win.', desc:'The platform tracks which messages, ads, and offers convert across every contractor. Your best plays get surfaced as recommendations with $ impact estimates. Get smarter every month without doing anything.' },
    { icon:'🗺️', title:'Live lead map', desc:'See every lead on a map. Spot which zip codes are profitable, batch nearby jobs, find ad-spend gaps. Real-time geographic intelligence.' },
    { icon:'📱', title:'Tech app in their pocket', desc:'Pre-van checklist, GPS-verified clock-in, job photos before/after, customer signature capture. Works offline. Installs like a real app, no App Store wait.' },
  ];
  return <section id="features" style={{ padding:'80px 32px', borderTop:`1px solid ${G.border}`, background:G.bgDeep || G.sidebar }}>
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <h2 style={{ fontSize:36, fontWeight:800, textAlign:'center', margin:'0 0 14px' }}>One platform. Everything you need.</h2>
      <p style={{ textAlign:'center', color:G.textMuted, maxWidth:560, margin:'0 auto 50px', fontSize:16 }}>Stop paying for five SaaS tools that don't talk to each other.</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:18 }}>
        {items.map((f,i) => <div key={i} style={{ ...styles.card, padding:24 }}>
          <div style={{ fontSize:28, marginBottom:10 }}>{f.icon}</div>
          <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>{f.title}</div>
          <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.55 }}>{f.desc}</div>
        </div>)}
      </div>
    </div>
  </section>;
}

function Pricing({ plans, go }) {
  return <section id="pricing" style={{ padding:'80px 32px', borderTop:`1px solid ${G.border}` }}>
    <div style={{ maxWidth:1180, margin:'0 auto' }}>
      <h2 style={{ fontSize:36, fontWeight:800, textAlign:'center', margin:'0 0 14px' }}>Pricing that scales with you.</h2>
      <p style={{ textAlign:'center', color:G.textMuted, fontSize:16, marginBottom:46 }}>Start free for 14 days. Switch plans anytime.</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:18 }}>
        {plans.map(p => <div key={p.key} style={{
          ...styles.card, padding:26, position:'relative',
          border: p.popular ? `2px solid ${G.purple}` : `1px solid ${G.border}`,
        }}>
          {p.popular && <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:G.purple, color:'#fff', fontSize:11, padding:'4px 12px', borderRadius:99, fontWeight:700 }}>MOST POPULAR</div>}
          <div style={{ fontWeight:800, fontSize:20 }}>{p.name}</div>
          <div style={{ marginTop:8 }}>
            {p.price != null
              ? <><span style={{ fontSize:42, fontWeight:800 }}>${p.price}</span><span style={{ color:G.textMuted, fontSize:14 }}>/mo</span></>
              : <span style={{ fontSize:32, fontWeight:800 }}>Let's talk</span>}
          </div>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>{p.blurb}</div>
          <ul style={{ marginTop:18, padding:0, listStyle:'none', fontSize:13, lineHeight:1.9 }}>
            {p.features?.map((f,i) => <li key={i}>{f}</li>)}
          </ul>
          {p.cta === 'contact'
            ? <Btn v="ghost" sz="md" full onClick={()=>{ window.location.href = 'mailto:sales@flowtradeopsautomation.com?subject=Custom%20plan%20inquiry'; }} style={{ marginTop:22 }}>Talk to sales</Btn>
            : <Btn v={p.popular?'primary':'ghost'} sz="md" full onClick={()=>go(p.key)} style={{ marginTop:22 }}>Start free trial</Btn>}
        </div>)}
      </div>
      <p style={{ textAlign:'center', color:G.textMuted, fontSize:13, marginTop:30 }}>
        All plans include unlimited SMS/email volume, Stripe payments at 0.2–0.5% platform fee, and US-based support.
      </p>
    </div>
  </section>;
}

function Testimonials() {
  const quotes = [
    { quote:`We replaced ServiceTitan, GoHighLevel, and our scheduling tool with TradeOps. Saved $1,200/mo and our close rate went up 18%.`, name:'Mike T.', biz:'Arctic HVAC, Tampa' },
    { quote:`The AI books leads while we're on jobs. Closed 4 deals last weekend without picking up the phone once.`, name:'Jake R.', biz:'FlowRight Plumbing' },
    { quote:`Good/Better/Best estimates lifted my average ticket from $1,400 to $2,100. That's the whole subscription back in a week.`, name:'Sarah C.', biz:'Bolt Electric' },
  ];
  return <section style={{ padding:'80px 32px', borderTop:`1px solid ${G.border}`, background:G.bgDeep || G.sidebar }}>
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <h2 style={{ fontSize:32, fontWeight:800, textAlign:'center', margin:'0 0 40px' }}>Trades owners who actually use it.</h2>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20 }}>
        {quotes.map((q,i) => <div key={i} style={{ ...styles.card, padding:24 }}>
          <div style={{ fontSize:16, lineHeight:1.6, fontStyle:'italic' }}>"{q.quote}"</div>
          <div style={{ marginTop:18, fontSize:13 }}>
            <div style={{ fontWeight:700 }}>{q.name}</div>
            <div style={{ color:G.textMuted }}>{q.biz}</div>
          </div>
        </div>)}
      </div>
    </div>
  </section>;
}

function Faq({ trialDays }) {
  const items = [
    { q:'Do I need a credit card to start?', a:`No. Your ${trialDays}-day trial starts the moment you sign up. We ask for a card only when you're ready to keep going.` },
    { q:'Can I use my own Stripe account?', a:'Yes. Stripe Connect plugs your account in via OAuth in about 60 seconds. Every customer payment lands directly in your bank — TradeOps only takes a small platform fee.' },
    { q:'What about Twilio for SMS?', a:'We provision a business number for you under our master Twilio account. You bring the brand; we handle 10DLC compliance, opt-out registry, STOP/HELP keywords — all the carrier paperwork.' },
    { q:'How fast can I get set up?', a:'First job booked: 15 minutes. Full migration from your old CRM: about a day. Snapshot library covers HVAC, plumbing, electrical, roofing, landscaping — pick a kit and ship.' },
    { q:'What if I outgrow my plan?', a:'Switch plans from the in-app billing tab. We prorate on the spot.' },
    { q:'Do you do agency / multi-location?', a:'Yes. Elite plan unlocks the Agency Console with multi-account push, snapshot library, ghost mode, and full white-label.' },
  ];
  return <section id="faq" style={{ padding:'80px 32px', borderTop:`1px solid ${G.border}` }}>
    <div style={{ maxWidth:780, margin:'0 auto' }}>
      <h2 style={{ fontSize:32, fontWeight:800, textAlign:'center', margin:'0 0 40px' }}>Frequently asked questions</h2>
      {items.map((f,i) => <div key={i} style={{ padding:'18px 0', borderBottom:`1px solid ${G.border}` }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>{f.q}</div>
        <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>{f.a}</div>
      </div>)}
    </div>
  </section>;
}

function Cta({ trialDays, go }) {
  return <section style={{ padding:'80px 32px', textAlign:'center' }}>
    <h2 style={{ fontSize:36, fontWeight:800, margin:'0 0 14px' }}>Ready to run your trades business on autopilot?</h2>
    <p style={{ color:G.textMuted, fontSize:16, marginBottom:26 }}>Start your {trialDays}-day free trial. No card required.</p>
    <Btn v="primary" sz="lg" onClick={()=>go()}>Start free trial →</Btn>
  </section>;
}

function Footer() {
  return <footer style={{ padding:'40px 32px', borderTop:`1px solid ${G.border}`, color:G.textMuted, fontSize:13 }}>
    <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
      <div>© {new Date().getFullYear()} TradeOps Inc.</div>
      <div style={{ display:'flex', gap:18 }}>
        <a href="/terms" style={navLink}>Terms</a>
        <a href="/privacy" style={navLink}>Privacy</a>
        <a href="mailto:support@flowtradeopsautomation.com" style={navLink}>Support</a>
      </div>
    </div>
  </footer>;
}

// Minimal static pages for Terms & Privacy so the links don't 404 at launch.
export function LegalPage({ kind }) {
  const isToS = kind === 'terms';
  const title = isToS ? 'Terms of Service' : 'Privacy Policy';
  return <div style={{ minHeight:'100vh', background:G.bg, color:G.text }}>
    <Nav/>
    <article style={{ maxWidth:760, margin:'0 auto', padding:'60px 32px', lineHeight:1.7, fontSize:15 }}>
      <h1 style={{ marginBottom:8 }}>{title}</h1>
      <p style={{ color:G.textMuted, marginTop:0 }}>Last updated: {new Date().toLocaleDateString()}</p>
      {isToS ? <TosBody/> : <PrivacyBody/>}
    </article>
    <Footer/>
  </div>;
}

function TosBody() {
  return <>
    <h3>1. The agreement</h3>
    <p>By creating a TradeOps account you agree to these Terms. If you do not agree, do not use the service. We may update these Terms — material changes are emailed 14 days before they take effect.</p>
    <h3>2. Your account</h3>
    <p>You are responsible for safeguarding your password and for any activity under your account. Notify us at support@flowtradeopsautomation.com of any unauthorized access.</p>
    <h3>3. Subscriptions, trials, and billing</h3>
    <p>Plans renew monthly. Trials convert to paid only after you add a card. You may cancel from the in-app billing tab at any time; access continues through the end of the current paid period. Failed payments may pause access until resolved.</p>
    <h3>4. Acceptable use</h3>
    <p>You will not use TradeOps to send unlawful, deceptive, or unsolicited messages, or to operate without legally required consent for SMS, email, or recorded calls in your jurisdiction. Repeated violations may result in suspension.</p>
    <h3>5. Customer data ownership</h3>
    <p>You own your business data and the contact data you bring into TradeOps. We process it on your behalf to operate the service. We do not sell customer data.</p>
    <h3>6. Service availability + SLAs</h3>
    <p>We target 99.5% monthly uptime. Scheduled maintenance is announced via the status page. We're not liable for downtime caused by upstream providers (Twilio, Stripe, SendGrid, ElevenLabs).</p>
    <h3>7. Limitation of liability</h3>
    <p>TradeOps is provided "as is." We are not liable for indirect or consequential damages. Total liability is capped at 12 months of fees paid.</p>
    <h3>8. Governing law</h3>
    <p>These Terms are governed by the laws of the State of Florida, USA.</p>
    <h3>9. Contact</h3>
    <p>support@flowtradeopsautomation.com</p>
  </>;
}

function PrivacyBody() {
  return <>
    <h3>1. What we collect</h3>
    <p>Account info (name, email, phone, business details), billing info (handled by Stripe — we never see your card), service data (contacts, jobs, messages, recordings, invoices), and usage logs needed to operate and secure the service.</p>
    <h3>2. How we use it</h3>
    <p>To deliver the product, send service-related communications, prevent abuse, and improve the platform. We do not sell personal information.</p>
    <h3>3. Sub-processors</h3>
    <p>Stripe (payments), Twilio (SMS + numbers), SendGrid (email), ElevenLabs (voice agents), Anthropic (AI), AWS / Cloudflare R2 (hosting + backups). Each is contractually bound to protect your data.</p>

    <h3>3a. Google User Data</h3>
    <p><strong>Use of Google APIs.</strong> If you connect a Google account (Gmail, Google Calendar, Google Drive, Google Ads, Google Business Profile), TradeOps's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
    <p><strong>What we access.</strong> Only the specific scopes you explicitly grant during OAuth consent. We never request scopes we don't actively use.</p>
    <p><strong>How we use Google data.</strong> Solely to provide the features you've enabled (e.g., sync calendar events to dispatch, send/receive Gmail with customers, push Google Ads spend to your dashboard). We do not use Google user data for advertising, do not sell it, do not transfer it to third parties except sub-processors necessary to provide the service, and do not allow humans to read it unless you've given explicit consent for specific support tickets, it's necessary for security, or it's required by law.</p>
    <p><strong>How to revoke.</strong> Disconnect any Google integration in TradeOps → Integrations, or revoke at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>. Revocation deletes the cached tokens immediately.</p>

    <h3>3b. Data Protection</h3>
    <p><strong>Encryption.</strong> All data in transit is encrypted via TLS 1.2+. Data at rest is encrypted on disk (AES-256). Database backups are encrypted before upload.</p>
    <p><strong>Access controls.</strong> Production access requires multi-factor authentication and is scoped to engineering staff with on-call responsibility. Every production access is logged.</p>
    <p><strong>Secret management.</strong> API keys, OAuth tokens, and credentials are stored in Fly.io secrets — never committed to source code or logged.</p>
    <p><strong>Incident response.</strong> We notify affected users within 72 hours of any confirmed breach involving personal data, including the nature of the data, the scope, and remediation steps.</p>

    <h3>4. Retention</h3>
    <p>We retain customer data while your account is active. On cancellation, we retain a minimal backup for 90 days then delete on request.</p>
    <h3>5. Your rights</h3>
    <p>Email privacy@flowtradeopsautomation.com to request export, correction, or deletion of your personal data. We respond within 30 days.</p>
    <h3>6. Children</h3>
    <p>TradeOps is for businesses. We do not knowingly collect information from anyone under 16.</p>
    <h3>7. Contact</h3>
    <p>privacy@flowtradeopsautomation.com</p>
  </>;
}
